import { resolveFiatOptionsForBalance } from "@/utils/fiatMarket";
import { resolveCfdSymbol } from "@/@xt/Nav/src/utils/getCfdSymbol";

/** 底部 bar / Nav 交易入口：U 本位占位符，实际由 resolveCfdSymbol 解析为法币或 U 本位 CFD */
const DEFAULT_CFD_USDT_SYMBOL = "btc_usdt_cfd";

function splitPathQueryHash(pathWithQuery) {
  if (!pathWithQuery || typeof pathWithQuery !== "string") {
    return { pathname: "", search: "", hash: "" };
  }
  let rest = pathWithQuery;
  let hash = "";
  const hi = rest.indexOf("#");
  if (hi >= 0) {
    hash = rest.slice(hi);
    rest = rest.slice(0, hi);
  }
  let pathname = rest;
  let search = "";
  const qi = rest.indexOf("?");
  if (qi >= 0) {
    pathname = rest.slice(0, qi);
    search = rest.slice(qi);
  }
  return { pathname, search, hash };
}

/**
 * 活动配置的 web 是否为「U 本位 CFD 合约页」路径（与 bottomBar ` /futures/contract/${cfdMarket}` 的默认 btc_usdt_cfd 一致）
 * 仅此时替换为 resolveCfdSymbol 结果；已配置 btc_sar_cfd 等则不处理。
 */
export function isDefaultUsdtCfdContractPath(pathOrUrl) {
  if (!pathOrUrl || typeof pathOrUrl !== "string") return false;
  let pathname = pathOrUrl.trim();
  try {
    if (/^https?:\/\//i.test(pathname)) {
      pathname = new URL(pathname).pathname;
    } else {
      const qi = pathname.indexOf("?");
      if (qi >= 0) pathname = pathname.slice(0, qi);
      const hi = pathname.indexOf("#");
      if (hi >= 0) pathname = pathname.slice(0, hi);
    }
  } catch {
    return false;
  }
  pathname = pathname.replace(/\/+$/, "") || "/";
  if (!pathname.startsWith("/")) pathname = `/${pathname}`;
  return /\/(?:cfd|futures)\/contract\/btc_usdt_cfd$/i.test(pathname);
}

/**
 * U 本位 CFD 活动跳转：与底部 bar 相同决策（法币余额 → fiatCurrencyIds → IP）
 */
export async function resolveActivityCfdContractWebPath(pathWithQuery, userInfo, loginStatus) {
  if (!pathWithQuery || typeof pathWithQuery !== "string") return pathWithQuery;
  const { pathname: pathOnly } = splitPathQueryHash(pathWithQuery);
  if (!isDefaultUsdtCfdContractPath(pathOnly)) return pathWithQuery;

  const symbol = await resolveCfdSymbol({
    fiatCurrencyIds: userInfo?.fiatCurrencyIds,
    loginStatus,
  });
  if (!symbol || symbol.toLowerCase() === DEFAULT_CFD_USDT_SYMBOL) return pathWithQuery;

  const { pathname, search, hash } = splitPathQueryHash(pathWithQuery);
  let normalized = pathname.replace(/\/+$/, "");
  if (!normalized.startsWith("/")) normalized = `/${normalized}`;
  const replaced = normalized.replace(/btc_usdt_cfd$/i, symbol);
  return `${replaced}${search}${hash}`;
}

/**
 * App 等活动配置的 U 本位页：`/futures/usd-m?market=btc_usdt_cfd&...`
 * 与 H5 `/futures/contract/btc_usdt_cfd` 一致，仅当 market 为默认占位时才按 resolveCfdSymbol 替换。
 */
function getPathnameSearchHashForJump(pathWithQuery) {
  if (!pathWithQuery || typeof pathWithQuery !== "string") {
    return { pathname: "", search: "", hash: "" };
  }
  const s = pathWithQuery.trim();
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      return { pathname: u.pathname, search: u.search, hash: u.hash };
    } catch {
      /* fall through */
    }
  }
  return splitPathQueryHash(s);
}

export function isDefaultUsdtCfdFuturesUsdMPath(pathWithQuery) {
  const { pathname, search } = getPathnameSearchHashForJump(pathWithQuery);
  let normalized = (pathname || "").replace(/\/+$/, "") || "/";
  if (!normalized.startsWith("/")) normalized = `/${normalized}`;
  if (!/^\/futures\/usd-m$/i.test(normalized)) return false;
  const qs = search.startsWith("?") ? search.slice(1) : search;
  const sp = new URLSearchParams(qs);
  const market = (sp.get("market") || "").trim().toLowerCase();
  if (!market) return false;
  return market === DEFAULT_CFD_USDT_SYMBOL;
}

/** AppRoute 用 /futures/usd-m：重写 query.market（保留 side 等其它参数） */
export async function resolveActivityFuturesUsdMJumpPath(pathWithQuery, userInfo, loginStatus) {
  if (!pathWithQuery || typeof pathWithQuery !== "string") return pathWithQuery;
  if (!isDefaultUsdtCfdFuturesUsdMPath(pathWithQuery)) return pathWithQuery;

  const symbol = await resolveCfdSymbol({
    fiatCurrencyIds: userInfo?.fiatCurrencyIds,
    loginStatus,
  });
  if (!symbol || symbol.toLowerCase() === DEFAULT_CFD_USDT_SYMBOL) return pathWithQuery;

  const { pathname, search, hash } = getPathnameSearchHashForJump(pathWithQuery);
  const qs = search.startsWith("?") ? search.slice(1) : search;
  const sp = new URLSearchParams(qs);
  sp.set("market", symbol);
  const q = sp.toString();
  if (/^https?:\/\//i.test(pathWithQuery.trim())) {
    try {
      const u = new URL(pathWithQuery.trim());
      u.search = q ? `?${q}` : "";
      return u.toString();
    } catch {
      /* fall through */
    }
  }
  return `${pathname}${q ? `?${q}` : ""}${hash || ""}`;
}

/**
 * 首页活动按钮：充值路径 + U 本位 CFD 路径统一解析（其它 URL 原样返回）
 */
export async function resolveActivityWebJumpPath(pathWithQuery, userInfo, loginStatus) {
  let next = pathWithQuery;
  next = await resolveActivityCfdContractWebPath(next, userInfo, loginStatus);
  next = await resolveActivityFuturesUsdMJumpPath(next, userInfo, loginStatus);
  next = await resolveActivityDepositWebPath(next, userInfo);
  return next;
}

/**
 * 活动配置的 web 跳转是否为「加密货币充值」路径（与 AssetBalance 的 USDT 充值路径一致）
 * 命中时才做法币分流；已是 /deposit/fiat 或其它 URL 不处理。
 */
export function isCryptoCommonDepositPath(pathOrUrl) {
  if (!pathOrUrl || typeof pathOrUrl !== "string") return false;
  let pathname = pathOrUrl.trim();
  try {
    if (/^https?:\/\//i.test(pathname)) {
      pathname = new URL(pathname).pathname;
    } else {
      const qi = pathname.indexOf("?");
      if (qi >= 0) pathname = pathname.slice(0, qi);
      const hi = pathname.indexOf("#");
      if (hi >= 0) pathname = pathname.slice(0, hi);
    }
  } catch {
    return false;
  }
  pathname = pathname.replace(/\/+$/, "") || "/";
  if (!pathname.startsWith("/")) pathname = `/${pathname}`;
  return /\/wallet\/account\/common\/deposit$/i.test(pathname);
}

/**
 * 首页活动「去充值」：jumpUrls.web 为加密货币充值时，按 recommendedCurrencyList 决定是否改为法币充值页。
 *
 * @param {string} pathWithQuery 例如 appendActivityDepositQueryToPath 之后的路径
 * @param {{ fiatCurrencyIds?: number[] }} userInfo XtContext userInfo
 * @returns {Promise<string>} 传给 Util.getUrl 的路径（相对路径 + query）
 */
export async function resolveActivityDepositWebPath(pathWithQuery, userInfo) {
  if (!pathWithQuery || typeof pathWithQuery !== "string") return pathWithQuery;
  const pathOnly = pathWithQuery.split("?")[0].split("#")[0];
  if (!isCryptoCommonDepositPath(pathOnly)) return pathWithQuery;

  const { primaryFiat } = await resolveFiatOptionsForBalance();

  if (!primaryFiat || primaryFiat === "USDT") return pathWithQuery;

  const qIndex = pathWithQuery.indexOf("?");
  const existingQs = qIndex >= 0 ? pathWithQuery.slice(qIndex + 1) : "";
  const sp = new URLSearchParams(existingQs);
  sp.set("currency", primaryFiat.toLowerCase());
  const q = sp.toString();
  return `/wallet/account/common/deposit/fiat${q ? `?${q}` : ""}`;
}
